import {
    BaseInput,
    BaseSelect,
    CoreFetch,
    CurrencyType,
    Invoice,
    InvoiceApi,
    InvoiceType,
    ISelectOption,
    ProviderPatientStatement,
    IToastContextProps,
} from '@enter/core';
import styled from '@emotion/styled/macro';
import { format } from 'date-fns';
import _ from 'lodash';
import React, { useState } from 'react';
import { IDialogContext } from './statement-details.types';
import * as S from './invoice-item-dialog.styles';

const FormSection = styled.div`
    margin-top: 16px;
`;

type RecordDiscountDialogProps = {
    patientStatement?: ProviderPatientStatement;
    onClose: () => void;
    onSuccess: () => void;
    dialogContext: IDialogContext;
    toastContext: IToastContextProps;
    equalsFilter: (term?: string | string[]) => string[] | undefined;
};

export const RecordDiscountDialog: React.FunctionComponent<RecordDiscountDialogProps> = (props) => {
    const { dialogContext, toastContext, equalsFilter, patientStatement, onClose, onSuccess } = props;

            // Initialize form state
            const [claimId, setClaimId] = useState<string>('');
            const [memo, setMemo] = useState<string>('Patient Loyalty Adjustment');
            const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
            const [errors, setErrors] = useState<Record<string, string>>({});
            const [baseAmount, setBaseAmount] = useState<string>('0');
            const [currentDiscount, setCurrentDiscount] = useState<string>('0');
            const [endingAmount, setEndingAmount] = useState<string>('0');
            const [isLoadingInvoice, setIsLoadingInvoice] = useState<boolean>(false);

            const claimOptions: ISelectOption[] = React.useMemo(() => {
                if (!patientStatement?.claims) return [];
                return patientStatement.claims.map(claim => ({
                    label: `${claim.externalId} - ${claim.fromDos ? format(new Date(claim.fromDos), 'MMM dd, yyyy') : ''}`,
                    value: claim.id,
                }));
            }, [patientStatement]);

            const validate = (): boolean => {
                const newErrors: Record<string, string> = {};

                if (!currentDiscount) {
                    newErrors.currentDiscount = 'Discount amount is required';
                }

                if (!claimId) {
                    newErrors.claimId = 'Claim is required';
                }

                if (!memo || memo.trim() === '') {
                    newErrors.memo = 'Discount memo is required';
                }

                setErrors(newErrors);
                return Object.keys(newErrors).length === 0;
            };

            const handleSubmit = async (): Promise<void> => {
                if (!validate()) {
                    return;
                }

                setIsSubmitting(true);

                try {
                    const invoices = await CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoices({
                        filterClaimId: equalsFilter(claimId),
                        filterInvoiceType: equalsFilter(InvoiceType.PATIENT),
                    });

                    if (!invoices || invoices.length === 0) {
                        throw new Error('No patient invoice found for the selected claim');
                    }

                    const invoiceId = invoices[invoices.length - 1].id;

                    const parsedAmount = parseFloat(currentDiscount);
                    const finalAmount = parsedAmount > 0 ? -parsedAmount : parsedAmount;

                    await CoreFetch<InvoiceApi>(InvoiceApi).postProviderInvoiceItem({
                        id: invoiceId,
                        createBillingInvoiceItemPayload: {
                            name: memo,
                            amount: {
                                amount: finalAmount.toString(),
                                currency: CurrencyType.USD,
                            },
                        },
                    });

                    toastContext.notify?.('Discount was recorded successfully.', 'right');
                    onSuccess();
                } catch (err) {
                    dialogContext.showError(err);
                } finally {
                    setIsSubmitting(false);
                }
            };

            const handleCancel = (): void => {
                onClose();
            };

            const handleClaimChange = async (option: any): Promise<void> => {
                const selectedClaimId = option?.value as string || '';
                setClaimId(selectedClaimId);
                
                if (!selectedClaimId) {
                    setBaseAmount('0');
                    setCurrentDiscount('0');
                    setEndingAmount('0');
                    return;
                }

                setIsLoadingInvoice(true);
                
                try {
                    const invoices = await CoreFetch<InvoiceApi>(InvoiceApi).listProviderInvoices({
                        filterClaimId: equalsFilter(selectedClaimId),
                        filterInvoiceType: equalsFilter(InvoiceType.PATIENT),
                    });

                    if (!invoices || invoices.length === 0) {
                        setBaseAmount('0');
                        setCurrentDiscount('0');
                        setEndingAmount('0');
                        return;
                    }

                    const invoice = invoices[invoices.length - 1];

                    const base = invoice?.amount?.amount || '0';
                    setBaseAmount(base);

                    // Initialize current discount to 0 (new discount to be entered)
                    setCurrentDiscount('0');

                    // Ending amount starts as base amount
                    setEndingAmount(base);

                } catch (err) {
                    dialogContext.showError(err);
                    setBaseAmount('0');
                    setCurrentDiscount('0');
                    setEndingAmount('0');
                } finally {
                    setIsLoadingInvoice(false);
                }
            };

            const handleCurrentDiscountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
                const discountValue = e.target.value;
                setCurrentDiscount(discountValue);
                
                const base = parseFloat(baseAmount) || 0;
                const discount = parseFloat(discountValue) || 0;
                const ending = base - discount;
                setEndingAmount(ending.toFixed(2));
            };

            const handleEndingAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
                const endingValue = e.target.value;
                setEndingAmount(endingValue);
                
                const base = parseFloat(baseAmount) || 0;
                const ending = parseFloat(endingValue) || 0;
                const discount = base - ending;
                setCurrentDiscount(discount.toFixed(2));
            };

            return (
                <S.DialogBody>
                    <FormSection>
                        <S.Label>Claim To Post Discount To</S.Label>
                        <BaseSelect
                            options={claimOptions}
                            value={claimOptions.find(opt => opt.value === claimId)}
                            onChange={handleClaimChange}
                            errorMessage={errors.claimId}
                            placeholder="Select a claim"
                            required
                        />
                    </FormSection>

                    <FormSection>
                        <S.Label>Discount Memo</S.Label>
                        <BaseInput
                            value={memo}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemo(e.target.value)}
                            errorMessage={errors.memo}
                            placeholder="Enter discount memo"
                            required
                        />
                    </FormSection>
                    <FormSection>
                        <S.Label>Base Invoice Amount</S.Label>
                        <BaseInput
                            type="number"
                            value={baseAmount}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBaseAmount(e.target.value)}
                            placeholder="0.00"
                            readOnly
                        />
                    </FormSection>
                    <FormSection>
                        <S.Label>Current Discount Amount</S.Label>
                        <BaseInput
                            type="number"
                            value={currentDiscount}
                            onChange={handleCurrentDiscountChange}
                            errorMessage={errors.currentDiscount}
                            placeholder="0.00"
                            required
                        />
                    </FormSection>
                    <FormSection>
                        <S.Label>Ending Invoice Amount</S.Label>
                        <BaseInput
                            type="number"
                            value={endingAmount}
                            onChange={handleEndingAmountChange}
                            placeholder="0.00"
                        />
                    </FormSection>

                    <S.ButtonGroup
                        changed={true}
                        useNotify={false}
                        onCancel={handleCancel}
                        onSave={handleSubmit}
                        saveLabel={isSubmitting ? 'Creating...' : 'Create Discount'}
                        saveNotice="Discount recorded successfully"
                    />
                </S.DialogBody>
            );
};

